/**
 * 支持的货币代码枚举
 */
export const CurrencyCode = { USD: "USD", CNY: "CNY", INR: "INR", EUR: "EUR", CENT: "CENT" } as const;

export type CurrencyCode = (typeof CurrencyCode)[keyof typeof CurrencyCode];

/**
 * 货币单位缩放体系
 * - INDIAN: 印度体系 (Lakh, Crore)
 * - INTERNATIONAL: 国际体系 (Million, Billion)
 * - NONE: 不缩放
 */
export type UnitSystem = "INDIAN" | "INTERNATIONAL" | "NONE";

/**
 * 单位缩放定义
 */
export interface UnitScale {
    symbol: string; // 单位符号 (e.g. K / L / C / M)
    value: number; // 阈值 (e.g. 1000 / 100000)
    decimalPlaces: number; // 保留小数位数
}

/**
 * 缩放后的金额结果
 */
export interface ScaledAmount {
    value: number; // 缩放后的数值
    unit?: string; // 单位 (若无单位则未缩放)
    decimalPlaces: number; // 建议保留的小数位数
}

/**
 * 货币配置项
 */
export interface CurrencyConfig {
    code: CurrencyCode;
    symbol: string;
    decimalPlaces: number; // 默认保留小数位数
    thousandSeparator: string; // 千分位分隔符
    decimalSeparator: string; // 小数点符号
    grouping: number[]; // 千分位分组规则 (e.g. [3] for 1,000,000; [3, 2] for 1,00,000)
    unitSystem?: UnitSystem; // 使用的单位缩放体系
}

export type ExchangeRateMap = { base: CurrencyCode; rates: Record<CurrencyCode, number> };

export interface BackendAmount {
    amount: number;
    currency: CurrencyCode;
}

export type CurrencySymbolDisplay = boolean | "prefix" | "suffix" | "only";
export type DecimalDisplayMode = "unlimited" | "max";

/**
 * 格式化控制选项
 */
export interface FormatOptions {
    /** 精度控制：若提供则强制小数位数，否则使用单位缩放推荐精度或默认精度 */
    precision?: number;
    /**
     * 动态小数位展示模式；仅在未传 precision 时生效
     * - unlimited: 展示内部全部精度，不补尾零
     * - max: 最多展示 maxPrecision 位小数，不补尾零
     */
    decimalDisplay?: DecimalDisplayMode;
    /** decimalDisplay 为 max 时的小数位上限；仅在未传 precision 时生效 */
    maxPrecision?: number;
    /** 是否使用千分位分割，默认为 true */
    useGrouping?: boolean;
    /** 是否使用单位缩放 (如 100k, 10M)，默认为 true */
    useScale?: boolean;
    /** 启用缩放的金额下限；未设置时默认 1_000_000（保持现有行为） */
    scaleMinAmount?: number;
    /** 货币符号展示方式，默认为 true，等价于前缀展示 */
    showCurrencySymbol?: CurrencySymbolDisplay;
}

/**
 * 货币处理完整选项
 */
export interface MoneyOptions extends FormatOptions {
    /** 是否进行汇率转换，默认为 true */
    useExchange?: boolean;
    /** 系统返回的数据即转换前的货币代码，默认为 INR */
    currency?: CurrencyCode;
    /** 最终展示的货币代码，默认为 INR */
    targetCurrency?: CurrencyCode;
}
